import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Input, Stack, Typography } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';
import { useTranslation } from 'react-i18next';

import { Search } from 'components/Search';
import { IconWrapper } from 'components/Icon';
import { ContentLayout } from 'website/components/Layout/components/ContentLayout';
import { NotFound } from 'components/NotFound/NotFound';
import { Pagination } from 'components/Pagination';
import { usePageSize } from 'hook/usePageSize';

import { useGetAll } from '../api/getAllJobAds';
import { ListItem } from '../components/JobAdsListItem';
import { Loader } from 'components/Loader';
import { DEFAULT_RADIUS_KM, PlaceRadiusPopover, formatPlaceRadius } from 'components/PlaceAutocomplete/PlaceRadiusPopover';
import { PlaceSearchUnavailableNotice, isPlaceSearchError } from 'components/PlaceAutocomplete/PlaceSearchUnavailableNotice';
import { useCompetenceAreas } from '../api/getCompetenceAreas';
import { MultiSelectDialog } from 'components/Form/MultiSelectDialog';
import { LoginSection } from 'website/components/LoginSection';
import BackgroundImage from '../assets/background.png';
import { Subscribe } from 'website/components/JobAdsSubscribeSection';
import { CardAutocomplete } from 'components/Form';
import { getCompanies } from 'features/frameworkAgreements/api/getCompanies';
import { JobAdsMapDialog } from 'components/JobAdsMap/JobAdsMapDialog';
import { isMapConfigured } from 'components/JobAdsMap/mapsConfig';
import { paths } from 'routes';

const CompanyItem = ({ Name, VAT, ...rest }) => (
  <Stack gap={1} alignItems={'flex-start !important'} {...rest}>
    <Typography variant="body1" fontWeight="bold">
      {Name} ({VAT})
    </Typography>
  </Stack>
);

const OWNER_TYPE_OPTIONS = [
  { id: 'Intermediator', label: 'Intermediator' },
  { id: 'DPS', label: 'DPS' },
  { id: 'Direct', label: 'Direct' },
];

export const List = () => {
  const { t, i18n } = useTranslation();
  const [isMapOpen, setIsMapOpen] = useState(false);
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialSearch = queryParams.get('search') || '';

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize();
  const [search, setSearch] = useState(initialSearch);
  const [filters, setFilters] = useState({
    includeInactive: false,
    includeExpired: false,
    sortBy: 'dateCreated',
    scope: 'all',
    competenceAreas: [],
    place: null,
    radiusKm: DEFAULT_RADIUS_KM,
    ownerTypes: [],
    buyerID: null,
    buyerName: '',
  });
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(null);
  const [isAreasDialogOpen, setIsAreasDialogOpen] = useState(false);
  const [selectedCompetenceAreas , setSelectedCompetenceAreas] = useState([]);
  const [isTypesDialogOpen, setIsTypesDialogOpen] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [isBuyerDialogOpen, setIsBuyerDialogOpen] = useState(false);
  const [draftBuyer, setDraftBuyer] = useState(null);
  const [placeAnchorEl, setPlaceAnchorEl] = useState(null);


  const { data: competences, isLoading: isAreasLoading } = useCompetenceAreas();

  const allAreas = useMemo(() => {
    if (!competences?.competences) return [];
    const mapData = (competences) => competences.map(item => ({
      id: item.id,
      label: item.name,
    }));

    return mapData(competences.competences);
  }, [competences]);  

  const handlePlaceApply = ({ place, radiusKm }) => {
    setFilters(prev => ({ ...prev, place, radiusKm }));
  };

  const selectedBuyer = useMemo(() => (
    filters.buyerID
      ? { company_id: filters.buyerID, Name: filters.buyerName, label: filters.buyerName }
      : null
  ), [filters.buyerID, filters.buyerName]);

  const handleBuyerConfirm = (newValue) => {
    setFilters((filters) => ({
      ...filters,
      buyerID: newValue ? newValue.company_id : null,
      buyerName: newValue ? newValue.Name : '',
    }));
  };

  const handleBuyerDialogOpen = () => {
    setDraftBuyer(selectedBuyer);
    setIsBuyerDialogOpen(true);
  };

  const handleBuyerDialogClear = () => {
    handleBuyerConfirm(null);
    setIsBuyerDialogOpen(false);
  };

  const handleBuyerDialogConfirm = () => {
    handleBuyerConfirm(draftBuyer);
    setIsBuyerDialogOpen(false);
  };

  const handleCompetenceAreasChange = (event) => {
    const { value } = event.target;
    const selectedIds = typeof value === 'string' ? value.split(',') : value;
    const newSelectedValues = allAreas.filter(option => selectedIds.includes(option.id));
    setSelectedCompetenceAreas(newSelectedValues);
  };

  const handleCompetenceAreasConfirmSelection= (value) => {
    const selectedIds = value.map(item => item.id);
    
    handleFilterUpdate({
      target: {
        name: 'competenceAreas',
        value: selectedIds,
        checked: null,
      }
    });

    setSelectedCompetenceAreas(value);
  };

  const handleCompetenceAreasDelete = (deletedId) => (event) => {
    event.stopPropagation();
    const updatedSelectedValues = selectedCompetenceAreas.filter((item) => item.id !== deletedId);
    setSelectedCompetenceAreas(updatedSelectedValues);
  };

  const handleTypesChange = (event) => {
    const { value } = event.target;
    const selectedIds = typeof value === 'string' ? value.split(',') : value;
    const newSelectedValues = OWNER_TYPE_OPTIONS.filter(option => selectedIds.includes(option.id));
    setSelectedTypes(newSelectedValues);
  };

  const handleTypesConfirmSelection = (value) => {
    const selectedIds = value.map(item => item.id);

    handleFilterUpdate({
      target: {
        name: 'ownerTypes',
        value: selectedIds,
        checked: null,
      }
    });

    setSelectedTypes(value);
  };

  const handleTypesDelete = (deletedId) => (event) => {
    event.stopPropagation();
    const updatedSelectedValues = selectedTypes.filter((item) => item.id !== deletedId);
    setSelectedTypes(updatedSelectedValues);
  };

  useEffect(() => {
    if (allAreas.length > 0) {
      const value = filters.competenceAreas;
      const selectedIds = typeof value === 'string' ? value.split(',') : value;
      const newSelectedValues = allAreas.filter(option => selectedIds.includes(option.id));

      setSelectedCompetenceAreas(newSelectedValues);
    }
  }, [filters.competenceAreas, allAreas])

  useEffect(() => {
    const value = filters.ownerTypes;
    const selectedIds = typeof value === 'string' ? value.split(',') : value;
    const newSelectedValues = OWNER_TYPE_OPTIONS.filter(option => selectedIds.includes(option.id));

    setSelectedTypes(newSelectedValues);
  }, [filters.ownerTypes])


  const {
    data: { jobAds, total, searchArea },
    refetch,
    isFetching,
    error: listError,
  } = useGetAll({
    search,
    ...filters,
    page: page - 1,
    limit: pageSize,
    queryOptions: {
      onSettled: () => {
        clearTimeout(timer);
        setTimeout(() => setLoading(false), 1000);
      },
    },
  });
  const debouncedSearchChange = useDebouncedCallback((search) => setSearch(search), 300);
  const handleSearchChange = ({ target: { value: search } }) => {
    debouncedSearchChange(search);
  };

  const handlePageChange = (_, page) => setPage(page);

  useRefetch();

  const caption = '';

  useEffect(() => {
    if (isFetching) {
      const timeoutId = setTimeout(() => setLoading(true), 500);
      setTimer(timeoutId);
    } else {
      clearTimeout(timer);
    }

    return () => clearTimeout(timer);
  }, [isFetching]);

  const handleFilterUpdate = ({ target: { name, value, checked } }) => {
    setFilters((filters) => {
      return { ...filters, [name]: value };
    });
  };

  const handleClearAll = () => {
    handlePlaceApply({ place: null, radiusKm: DEFAULT_RADIUS_KM });
    handleCompetenceAreasConfirmSelection([]);
    handleTypesConfirmSelection([]);
    handleBuyerConfirm(null);
  }

  const isLoading = isAreasLoading;

  return (
    <ContentLayout>
      <Stack height={1} width={1} sx={{ alignSelf: "center"}} /* gap={1} */>
        <Stack
          pb={2}
          bgcolor="background.paper"
          // borderTop="1px solid"
          // borderColor="border.main"
          height="100%"
        >
          <Stack
            direction="column"
            gap={6.25}
            alignItems="center"
            justifyContent={"space-between"}
            pt={6.25}
            sx={{
              backgroundImage: `url(${BackgroundImage})`,
              backgroundColor: 'grey.300',
              backgroundPosition: 'center',
              backgroundSize: "cover",
              backgroundRepeat: 'no-repeat',
              position: "relative",
              ml: "-50vw",
              left: "50%",
              width: "100vw",
              minHeight: 300,
              height: {xs: 1, md: 1},
            }}
            minWidth={256}
          >
            <Stack
              direction="column"
              gap={2.5}
              alignItems="center"
              justifyContent={"space-between"}
              width={1}
              maxWidth={{xs:664, md: 1200}}
              sx={{
                boxSizing: 'border-box',
                px: {xs: 4, md: 6},
              }}
            >
              <Search
                placeholder={t('website:jobsList.searchPlaceholder')}
                handleSearchChange={handleSearchChange}
                initialValue={initialSearch}
                sx={{
                  minWidth: 200,
                  height: 40,
                  py: 2.5,
                  px: 3.5,
                  borderRadius: 1,
                  alignSelf: "stretch",
                  border: "1px solid",
                  borderColor:"#13373F33",
                  backgroundColor: "background.paper",
                  boxShadow: "0 12px 40px 0 rgba(0, 0, 0, 0.08)",
                }}
                inputSx={{
                  border: "none",
                  height: 1,
                  '& .MuiOutlinedInput-input': {
                    fontSize: "1rem",
                    fontWeight: 400,
                    fontFeatureSettings: `'liga' off, 'clig' off`,
                    color: 'text.black',
                    lineHeight: '24px',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: "text.placeholder",
                    fontFeatureSettings: '"liga" off, "cling" off',
                    fontSize: '1rem',
                    fontWeight: 400,
                    lineHeight: '24px',
                  },
                  pl: 0,
                  pr: 0,
                }}
                iconSx={{
                  svg: {
                    height: 24,
                    color: 'text.black',
                    stroke: "#000",
                    strokeWidth: 0,
                  }
                }}
              />
              <Stack direction={"row"} gap={2} alignItems={"flex-start"} width={1} sx={{flexWrap: "wrap"}}>
                <Box>
                  <Button
                    variant="outlined"
                    onClick={(event) => setPlaceAnchorEl(event.currentTarget)}
                    disabled={isLoading}
                    sx={{
                      backgroundColor: "background.paper",
                      borderRadius: 1,
                      fontWeight: 400,
                      lineHeight: "24px",
                      fontFeatureSettings: '"liga" off, "cling" off',
                      boxShadow: "0 2px 4px 0 rgba(19, 55, 63, 0.20)",
                    }}
                  >
                    {t('website:jobsList.filters.location')} {filters.place && `(${formatPlaceRadius(filters.place, filters.radiusKm)})`}
                    <IconWrapper
                      icon="chevronDown"
                      sx={{
                        height: 14,
                        width: 14,
                        svg: {
                          stroke: "#000",
                          strokeWidth:1,
                          height: 14,
                          width: 14,
                          mt: -1.25,
                        },
                        ml: 1,
                      }}
                      height={14}
                    />
                  </Button>
                  <PlaceRadiusPopover
                    anchorEl={placeAnchorEl}
                    onClose={() => setPlaceAnchorEl(null)}
                    place={filters.place}
                    radiusKm={filters.radiusKm}
                    onApply={handlePlaceApply}
                    isPublic
                    lang={i18n.language?.startsWith('sv') ? 'sv' : 'en'}
                    texts={{
                      radius: t('website:jobsList.placeSearch.radius'),
                      apply: t('website:jobsList.placeSearch.apply'),
                      clear: t('website:jobsList.placeSearch.clear'),
                      cancel: t('website:jobsList.placeSearch.cancel'),
                    }}
                    placeTexts={{
                      placeholder: t('website:jobsList.placeSearch.placeholder'),
                      typeMore: t('website:jobsList.placeSearch.typeMore'),
                      noResults: t('website:jobsList.placeSearch.noResults'),
                      unavailable: t('website:jobsList.placeSearch.unavailable'),
                      rateLimited: t('website:jobsList.placeSearch.rateLimited'),
                      failed: t('website:jobsList.placeSearch.failed'),
                      poweredBy: t('website:jobsList.placeSearch.poweredBy'),
                    }}
                  />
                </Box>
                <Box>
                  <Button
                    variant="outlined"
                    onClick={() => setIsAreasDialogOpen(true)}
                    disabled={isLoading}
                    sx={{
                      backgroundColor: "background.paper",
                      borderRadius: 1,
                      fontWeight: 400,
                      lineHeight: "24px",
                      fontFeatureSettings: '"liga" off, "cling" off',
                      boxShadow: "0 2px 4px 0 rgba(19, 55, 63, 0.20)",
                    }}
                  >
                    {t('website:jobsList.filters.competenceAreas')} {selectedCompetenceAreas.length > 0 && `(${selectedCompetenceAreas.length}/${allAreas.length > 0 ? allAreas.length : '...'})`}
                    <IconWrapper
                      icon="chevronDown"
                      sx={{
                        height: 14,
                        width: 14,
                        svg: {
                          stroke: "#000",
                          strokeWidth:1,
                          height: 14,
                          width: 14,
                          mt: -1.25,
                        },
                        ml: 1,
                      }}
                      height={14}
                    />
                  </Button>
                  <MultiSelectDialog
                    name = 'Comptenece Areas'
                    open={isAreasDialogOpen}
                    onClose={() => setIsAreasDialogOpen(false)}
                    onConfirm={handleCompetenceAreasConfirmSelection}
                    onChange={handleCompetenceAreasChange}
                    onDelete={handleCompetenceAreasDelete}
                    value={selectedCompetenceAreas}
                    treeData={allAreas}
                  />
                </Box>
                <Box>
                  <Button
                    variant="outlined"
                    onClick={() => setIsTypesDialogOpen(true)}
                    disabled={isLoading}
                    sx={{
                      backgroundColor: "background.paper",
                      borderRadius: 1,
                      fontWeight: 400,
                      lineHeight: "24px",
                      fontFeatureSettings: '"liga" off, "cling" off',
                      boxShadow: "0 2px 4px 0 rgba(19, 55, 63, 0.20)",
                    }}
                  >
                    {t('website:jobsList.filters.type')} {selectedTypes.length > 0 && `(${selectedTypes.length}/${OWNER_TYPE_OPTIONS.length})`}
                    <IconWrapper
                      icon="chevronDown"
                      sx={{
                        height: 14,
                        width: 14,
                        svg: {
                          stroke: "#000",
                          strokeWidth:1,
                          height: 14,
                          width: 14,
                          mt: -1.25,
                        },
                        ml: 1,
                      }}
                      height={14}
                    />
                  </Button>
                  <MultiSelectDialog
                    name = 'Type'
                    open={isTypesDialogOpen}
                    onClose={() => setIsTypesDialogOpen(false)}
                    onConfirm={handleTypesConfirmSelection}
                    onChange={handleTypesChange}
                    onDelete={handleTypesDelete}
                    value={selectedTypes}
                    treeData={OWNER_TYPE_OPTIONS}
                  />
                </Box>
                <Box>
                  <Button
                    variant="outlined"
                    onClick={handleBuyerDialogOpen}
                    disabled={isLoading}
                    sx={{
                      backgroundColor: "background.paper",
                      borderRadius: 1,
                      fontWeight: 400,
                      lineHeight: "24px",
                      fontFeatureSettings: '"liga" off, "cling" off',
                      boxShadow: "0 2px 4px 0 rgba(19, 55, 63, 0.20)",
                    }}
                  >
                    {t('website:jobsList.filters.buyer')} {filters.buyerName && `(${filters.buyerName})`}
                    <IconWrapper
                      icon="chevronDown"
                      sx={{
                        height: 14,
                        width: 14,
                        svg: {
                          stroke: "#000",
                          strokeWidth:1,
                          height: 14,
                          width: 14,
                          mt: -1.25,
                        },
                        ml: 1,
                      }}
                      height={14}
                    />
                  </Button>
                  <Dialog open={isBuyerDialogOpen} onClose={() => setIsBuyerDialogOpen(false)} fullWidth maxWidth="sm">
                    <DialogTitle>{t('website:jobsList.filters.buyer')}</DialogTitle>
                    <DialogContent>
                      <CardAutocomplete
                        name="buyerFilter"
                        label={t('website:jobsList.filters.buyer')}
                        placeholder={t('website:jobsList.filters.buyerSearchPlaceholder')}
                        queryFunction={(search) => getCompanies('buyer', search, false, true, true)}
                        optionLabelName="Name"
                        ItemCard={CompanyItem}
                        onChange={setDraftBuyer}
                        value={draftBuyer}
                        skipCreatingShadow={true}
                        fetchOnOpen={true}
                        fullWidth
                        changeSelectedValueOnChange={true}
                      />
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={handleBuyerDialogClear}>{t('website:jobsList.filters.clearAll')}</Button>
                      <Button onClick={() => setIsBuyerDialogOpen(false)}>{t('website:jobsList.filters.cancel')}</Button>
                      <Button onClick={handleBuyerDialogConfirm} variant="contained">{t('website:jobsList.filters.confirm')}</Button>
                    </DialogActions>
                  </Dialog>
                </Box>
                {isMapConfigured() && jobAds?.length > 0 && (
                  <Button
                    variant="outlined"
                    onClick={() => setIsMapOpen(true)}
                    disabled={isLoading}
                    sx={{
                      backgroundColor: "background.paper",
                      borderRadius: 1,
                      fontWeight: 400,
                      lineHeight: "24px",
                      fontFeatureSettings: '"liga" off, "cling" off',
                      boxShadow: "0 2px 4px 0 rgba(19, 55, 63, 0.20)",
                      color: 'text.primary',
                      borderColor: 'border.jobAd',
                    }}
                  >
                    <IconWrapper
                      icon="map"
                      sx={{
                        height: 16,
                        width: 16,
                        svg: {
                          stroke: "#000",
                          strokeWidth: 1.5,
                          height: 16,
                          width: 16,
                          mt: -1,
                        },
                        mr: 1,
                      }}
                      height={16}
                    />
                    {t('website:jobsList.map.show')}
                  </Button>
                )}
                <JobAdsMapDialog
                  open={isMapOpen}
                  onClose={() => setIsMapOpen(false)}
                  title={t('website:jobsList.map.title')}
                  closeLabel={t('website:jobsList.map.close')}
                  jobAds={jobAds ?? []}
                  searchArea={searchArea}
                  getAdUrl={(ad) => paths.public.jobAdsDetailsView(ad.slug, i18n.language?.startsWith('sv') ? 'sv' : 'en')}
                  lang={i18n.language?.startsWith('sv') ? 'sv' : 'en'}
                  texts={{
                    unmappedAds: (count) => t('website:jobsList.map.unmappedAds', { count }),
                    noLocations: t('website:jobsList.map.noLocations'),
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={handleClearAll}
                  disabled={isLoading}
                  sx={{
                    backgroundColor: "background.subscribeInput",
                    borderRadius: 1,
                    fontWeight: 400,
                    lineHeight: "24px",
                    fontFeatureSettings: '"liga" off, "cling" off',
                    boxShadow: "0 2px 4px 0 rgba(19, 55, 63, 0.20)",
                  }}
                >
                  <IconWrapper
                    icon="xMark"
                    sx={{
                      height: 14,
                      width: 14,
                      svg: {
                        stroke: "#000",
                        strokeWidth:1,
                        height: 14,
                        width: 14,
                        mt: -1.25,
                      },
                      mr: 1,
                    }}
                      height={14}
                    />
                    {t('website:jobsList.filters.clearAll')}
                </Button>
              </Stack>
              <Subscribe />
            </Stack>
            {/* <LoginSection /> */}
          </Stack>

          {filters.place && isPlaceSearchError(listError) && (
            <PlaceSearchUnavailableNotice
              message={t('website:jobsList.placeSearch.unavailable')}
              clearLabel={t('website:jobsList.placeSearch.clearLocation')}
              onClear={() => handlePlaceApply({ place: null, radiusKm: DEFAULT_RADIUS_KM })}
            />
          )}

          {isFetching && !loading ? 
          (
            <></>
          ) :
          (!isFetching || (isFetching && loading)) ?
          (
            <>
            {(loading) && (
              <Loader />
            )}

            {!loading && (
            <>
            {!jobAds && (
              <Stack
                height="100%"
                minHeight={250}
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="h5">{t('website:jobsList.emptyState.title')}</Typography>
              </Stack>
            )}

            {jobAds && (
              <>
              <Stack
                flex={1}
                py={3.25}
                px={{xs: 4, md: 6}}
                bgcolor="background.paper"
                gap={3.25}
                minWidth={256}
              >
                {jobAds.length === 0 && (
                  <NotFound
                    wrapperSx={{ mt: 19, minHeight: 250 }}
                    title={t('website:jobsList.emptyState.title')}
                    caption={caption}
                  />
                )}
                {jobAds.length !== 0 &&
                  jobAds.map((item) => (
                    <ListItem key={item.ad_id} {...item} />
                  ))}
              </Stack>
            </>
          )}

            <Stack direction="row" justifyContent="center">
              <Pagination
                page={page}
                handlePageChange={handlePageChange}
                itemCount={total}
                itemsPerPage={pageSize}
                onItemsPerPageChange={setPageSize}
                itemsPerPageLabel={t('website:common.pagination.perPage')}
                prevLabel={t('website:common.pagination.prev')}
                nextLabel={t('website:common.pagination.next')}
              />
            </Stack>
            </>
          )}
          </>
        ) :
        (
          <></>
        )}
        </Stack>
      </Stack>
    </ContentLayout>
  );

  function useRefetch() {
    useEffect(() => {
      refetch();
    }, [page, pageSize, filters, search]);
  }
};

